/**
 * threadSummaryName — a thread's name is a NAME, never a sentence
 * (Miles, 2026-09-20: "they really need to be shorter thread summary names in
 * general").
 *
 * THE FINDING behind this module. The header showed
 * `Triage: prod-e2e: video-add-sound (testing-account) failed — "add sound to
 * this clip" rides generate_sound…` — about 140 characters — and every surface
 * that has to show a thread's name was already built for a fraction of that:
 *
 *   · a person's own rename caps at 42 (`THREAD_NAME_MAX_LEN`)
 *   · the header's full-reveal, the escape hatch from the clamp, bounds at
 *     four lines ≈ 100 characters
 *   · a switcher chip shows perhaps 20 before it ellipsizes
 *
 * while the platform's own machine-made names were bounded at 120 and 200. So
 * the names were roughly three times what anything could display, and the
 * "squished title" reports were the SYMPTOM — no header layout can show a
 * paragraph. A machine-composed name is bounded here, once, at a width the
 * surfaces actually have.
 *
 * WHY A WORD BOUNDARY, not a slice. `…(testin` and `rides generate_sound` are
 * both mid-word cuts, and a mid-word cut reads as corruption rather than as
 * brevity — the eye cannot tell a truncated name from a broken one. The trim
 * walks back to the last space when doing so keeps most of the budget, and
 * drops the dangling punctuation an English clause leaves behind (`—`, `:`,
 * `,`) so the result ends the way a name ends.
 *
 * WHAT THIS IS NOT. It never invents, re-orders or summarises words: a name
 * that already fits comes back byte-identical, so nothing a person chose is
 * ever rewritten, and the ellipsis appears ONLY when characters were actually
 * dropped. The model-written summary is still the model's; this bounds it.
 *
 * Its server twin is `functions/src/prest0nSession/threadSummaryName.ts`,
 * pinned by a behaviour table in both directions — both sides bound the SAME
 * stored strings, so a rule that held on one side only would still write the
 * 140-character name the other refuses to show.
 */
import { boundByCodePoints, countCodePoints } from '../../utils/textBounds';

/**
 * The bound for a name the PLATFORM composed — a triage report's subject, the
 * labeler's topic summary, any future auto-title.
 *
 * 56, not 42: a machine name carries context a person's would not ("Triage:",
 * a workspace, a failing check), and 42 cut those before the subject arrived.
 * 56 is about one line of the chat header at a mid-width pane and still well
 * inside the header's four-line reveal, so a bounded name is readable
 * everywhere without the reveal — which is the point. Deliberately NOT the
 * 42 a person types: a name someone chose is theirs and is never re-bounded.
 */
export const THREAD_SUMMARY_MAX_LEN = 56;

/**
 * How far back the trim will walk to land on a word boundary. Past this it
 * takes the hard cut instead — otherwise a name with one very long token
 * ("prest0n-vm-testing-account-us-central1-a…") would lose most of its budget
 * chasing a space that is not there.
 */
const WORD_WALK_BUDGET = 14;

/** Punctuation an English clause dangles once its tail is cut. */
const DANGLING = /[\s ,;:.!?/|\-–—(["'“‘«]+$/u;

/** An opening bracket with nothing to close it after the cut. */
const UNCLOSED_OPENER = /\s*[([{«"“'‘][^)\]}»"”'’]*$/u;

export interface BoundThreadSummaryOptions {
  /** Bound in CODE POINTS. Defaults to THREAD_SUMMARY_MAX_LEN. */
  max?: number;
  /**
   * What marks a name that was cut. '' drops it entirely — for a surface that
   * shows its own overflow affordance. Counted INSIDE the bound, so the result
   * never exceeds `max`.
   */
  ellipsis?: string;
}

/**
 * Bound a machine-composed thread name to something a header can show.
 *
 * Returns the input untouched (trimmed, single-spaced) when it already fits —
 * no ellipsis, no rewriting.
 */
export function boundThreadSummaryName(
  raw: unknown,
  opts: BoundThreadSummaryOptions = {},
): string {
  const max = Number.isFinite(opts.max as number) && (opts.max as number) > 0
    ? Math.floor(opts.max as number)
    : THREAD_SUMMARY_MAX_LEN;
  const ellipsis = opts.ellipsis === undefined ? '…' : opts.ellipsis;

  const value = (typeof raw === 'string' ? raw : '').replace(/\s+/g, ' ').trim();
  if (!value) return '';
  if (countCodePoints(value) <= max) return value;

  // Leave room for the mark, so the RESULT honours `max` rather than max+1.
  //
  // A budget smaller than the mark itself has no honest answer: at max 1 the
  // result would be one character PLUS an ellipsis, i.e. two, which breaks the
  // one property every caller relies on. Below that floor the name is bounded
  // with NO mark — a bound that overruns is worse than one that is silent, and
  // no surface asks for a name this short anyway.
  const markLen = countCodePoints(ellipsis);
  if (max <= markLen) return boundByCodePoints(value, max);
  const budget = max - markLen;
  let cut = boundByCodePoints(value, budget);

  // Walk back to a word boundary when one is close enough to be worth it.
  const lastSpace = cut.lastIndexOf(' ');
  if (lastSpace > 0 && countCodePoints(cut) - countCodePoints(cut.slice(0, lastSpace)) <= WORD_WALK_BUDGET) {
    cut = cut.slice(0, lastSpace);
  }

  // An opener with no closer reads as a broken name, not a short one.
  const withoutOrphanOpener = cut.replace(UNCLOSED_OPENER, '');
  if (withoutOrphanOpener.trim()) cut = withoutOrphanOpener;

  cut = cut.replace(DANGLING, '');
  if (!cut.trim()) {
    // Everything was punctuation — the hard cut is the honest answer.
    cut = boundByCodePoints(value, budget);
  }
  return `${cut}${ellipsis}`;
}

/** Did bounding this name actually drop anything? (For a tooltip that offers the rest.) */
export function threadSummaryNameWasCut(raw: unknown, opts: BoundThreadSummaryOptions = {}): boolean {
  const value = (typeof raw === 'string' ? raw : '').replace(/\s+/g, ' ').trim();
  const max = Number.isFinite(opts.max as number) && (opts.max as number) > 0
    ? Math.floor(opts.max as number)
    : THREAD_SUMMARY_MAX_LEN;
  return countCodePoints(value) > max;
}
